<template>
  <div id="recommendation-panel">
    <b-card id="recommendation" class="shadow rounded">
      <b-card-title>Selected dose simulation regimen</b-card-title>
      <b-card-body>
        <p>
          <label class="text-muted font-italic"
            ><i>Simulation may include a loading dose, if provided by the model.</i></label
          >
        </p>
        <span class="title text-success">{{ recommendation }} </span>
        <button class="right border-0" title="Copy to clipboard" @click="copyRecommendation">
          <i class="bi bi-clipboard"></i>
        </button>
      </b-card-body>
    </b-card>
    <b-card class="shadow rounded">
      <b-card-title style="cursor: pointer" @click="visible = !visible"
        >PK/PD parameters
        <i class="bi bi-chevron-double-right"></i>
      </b-card-title>
      <b-collapse v-model="visible" class="mt-2">
        <b-card-body>
          <b-row>
            <b-col>
              <div class="title">PK params</div>
              <PkParams />
            </b-col>
            <b-col>
              <div class="title">Simulation</div>
              <b-table striped fixed small responsive="sm" :items="items" :fields="fields">
                <template #cell(unit)="{ item }"
                  ><span v-html="displaySIUnits(item.unit)"></span
                ></template>
              </b-table>
            </b-col>
          </b-row>
        </b-card-body>
      </b-collapse>
    </b-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import PkParams from '@/components/display/PkParams.vue';
import { displaySIUnits } from '@/models/utils/constants';
import { useDosingStore } from '@/stores/dosing';
import { useToast } from 'vue-toastification';
import moment from 'moment/moment';

defineOptions({ name: 'Recommendation' });

const store = useDosingStore();
const visible = ref(false);
const toast = useToast();

const fields = [
  { key: 'label', sortable: true },
  { key: 'value', sortable: true },
  { key: 'unit', sortable: true },
];

function nformat(value: number, decimals: number): string {
  return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals).toFixed(decimals);
}

async function copyRecommendation() {
  try {
    await navigator.clipboard.writeText(recommendation.value);
    toast.success('Copied: ' + recommendation.value);
  } catch {
    toast.error('Cannot copy citation');
  }
}

const recommendation = computed(() => {
  const selectedPlan = store.getSelectedPlan;
  if (selectedPlan) {
    const drug = store.getDrug;
    const drugName = drug?.name ?? 'drug';
    const loading = selectedPlan.doseRegime.loadingDose;
    const loadingDuration = drug?.maxDosePerHr
      ? loading / drug.maxDosePerHr
      : selectedPlan.doseRegime.deliveryMethod === 'CONT'
        ? 6
        : selectedPlan.doseRegime.infusionDuration;
    const load =
      loading && loading !== selectedPlan.doseRegime.dose
        ? `${loading} mg loading over ${loadingDuration} hours then `
        : '';
    const dose = selectedPlan.doseRegime.dose ?? 'x';
    const interval = selectedPlan.doseRegime.interval ?? 'x';
    const infusion_duration = selectedPlan.doseRegime.infusionDuration ?? 'x';
    const delivery = selectedPlan.doseRegime.deliveryMethod;
    const startTime = selectedPlan.doseRegime.startTime
      ? `Starting: ${moment(selectedPlan.doseRegime.startTime).format('DD MMM YYYY HH:mm')}. `
      : '';

    let outcomeText = '';
    const r = selectedPlan.mainResult;
    if (delivery === 'CONT') {
      if (r?.ss != null)
        outcomeText = ` Predicted steady-state concentration: ${nformat(r.ss, 2)} mg/L.`;
    } else {
      const target = store.getTarget;
      const targetType = target?.target ?? '';
      if (targetType === 'AUC' && r?.auc != null) {
        outcomeText = ` Predicted AUC₂₄: ${nformat(r.auc, 2)} mg·hr/L.`;
      } else if (targetType === 'Trough' && r?.trough != null) {
        outcomeText = ` Predicted trough: ${nformat(r.trough, 2)} mg/L.`;
      } else if (r?.auc != null) {
        outcomeText = ` Predicted AUC₂₄: ${nformat(r.auc, 2)} mg·hr/L.`;
      }
    }

    const regimen =
      delivery === 'CONT'
        ? `${drugName} ${load} ${dose} mg over 24 hours continuous IV infusion`
        : delivery === 'ORAL'
          ? `${drugName} ${load}${dose} mg ${delivery} every ${interval} hours`
          : `${drugName} ${load}${dose} mg ${delivery} every ${interval} hours with ${infusion_duration} hours infusion`;
    return `${startTime}${regimen}.${outcomeText}`;
  }
  return '';
});

const items = computed(() => {
  const selectedPlan = store.getSelectedPlan;
  if (selectedPlan) {
    return [
      {
        label: 'AUC',
        value: selectedPlan.mainResult.auc ? nformat(selectedPlan.mainResult.auc, 2) : 'x',
        unit: selectedPlan.mainResult.auc ? 'mg·hr/L' : '',
      },
      {
        label: 'Peak',
        value: selectedPlan.mainResult.peak ? nformat(selectedPlan.mainResult.peak, 2) : 'x',
        unit: selectedPlan.mainResult.peak ? 'mg/L' : '',
      },
      {
        label: 'Trough',
        value: selectedPlan.mainResult.trough ? nformat(selectedPlan.mainResult.trough, 2) : 'x',
        unit: selectedPlan.mainResult.trough ? 'mg/L' : '',
      },
      {
        label: 'Loading',
        value: selectedPlan.doseRegime.loading ?? 'x',
        unit: selectedPlan.doseRegime.loading ? 'mg' : '',
      },
      {
        label: 'Maintenance',
        value: selectedPlan.doseRegime.dose ?? 'x',
        unit: selectedPlan.doseRegime.dose ? 'mg' : '',
      },
      { label: 'Interval', value: selectedPlan.doseRegime.interval ?? 'x', unit: 'hours' },
      {
        label: 'Infusion duration',
        value: selectedPlan.doseRegime.infusionDuration ?? 'x',
        unit: 'hours',
      },
      {
        label: 'Steady state',
        value: selectedPlan.mainResult.ss ? nformat(selectedPlan.mainResult.ss, 2) : 'x',
        unit: selectedPlan.mainResult.ss ? 'mg/L' : '',
      },
      {
        label: 'fT > MIC',
        value: selectedPlan.mainResult.tmic ? nformat(selectedPlan.mainResult.tmic, 2) : 'x',
        unit: selectedPlan.mainResult.tmic ? '%' : '',
      },
    ];
  }
  return [];
});
</script>

<style scoped>
#recommendation-panel {
  margin-top: -2em;
}

.title {
  font-weight: bold;
}
</style>
